import React, { useEffect, useMemo, useState } from 'react';
import { hasLinks } from '../../utils/entityHubRenderable';
import { partitionPersonCuriosities } from '../../utils/entityHubPersonContent';
import HubLinkBadges from './HubLinkBadges.jsx';
import HubRemindButton from './HubRemindButton.jsx';
import HubFollowButton from './HubFollowButton.jsx';
import { resolvePersonHeroCover, pickLiveHeroEvent, personHubHasSurface, streamingLinksFromLiveEvent } from '../../utils/personHeroCover';
import { upcomingConcertsFromHub } from '../../utils/concertEvents';
import { fetchKickChannelBio, kickSlugFromHub, personHubHasKickIdentity } from '../../utils/kickChannelBio';

function PersonAboutOverlay({ about }) {
  if (!about?.length) return null;
  return (
    <aside className="entity-hub-person-about-overlay">
      <h3 className="entity-hub-sidebar-title">About</h3>
      {about.map((block) => (
        <p key={block.id} className="entity-hub-about-text">
          {block.url ? (
            <a href={block.url} target="_blank" rel="noopener noreferrer">
              {block.body}
            </a>
          ) : (
            block.body
          )}
        </p>
      ))}
    </aside>
  );
}

function PersonHero({ hub, results = [], entityId }) {
  const entity = hub.entity || {};
  const cover = resolvePersonHeroCover(hub, results);
  const coverSrc = cover.src;
  const remindEvent = upcomingConcertsFromHub(hub)[0] || null;
  const showFollow = Boolean(entityId) && personHubHasKickIdentity(hub, results);
  const liveMeta = cover.kind === 'live';
  const metaClass = `entity-hub-hero-meta${liveMeta ? ' entity-hub-hero-meta--live' : ''}`;
  const liveEvent = pickLiveHeroEvent(results, hub);
  const liveTint = liveMeta
    && ['kick', 'twitch', 'youtube'].includes(String(liveEvent?.platform || '').toLowerCase())
    ? String(liveEvent.platform).toLowerCase()
    : null;
  const coverLinks = hasLinks(hub)
    ? hub.links
    : streamingLinksFromLiveEvent(liveEvent);
  const showLinks = coverLinks.length > 0;
  const kickSlug = useMemo(() => kickSlugFromHub(hub, results), [hub, results]);
  const hasCreatorChannel = Boolean(kickSlug) || Boolean(liveEvent);
  const hubAbout = useMemo(
    () => partitionPersonCuriosities(hub, {
      preferredPlatform: liveEvent?.platform,
      hasCreatorChannel,
    }).about,
    [hub, liveEvent, hasCreatorChannel]
  );
  const hasKickAbout = hubAbout.some((block) => block.source === 'kick');
  const [kickBio, setKickBio] = useState(null);

  useEffect(() => {
    setKickBio(null);
    if (hasKickAbout || !kickSlug) return undefined;
    let cancelled = false;
    fetchKickChannelBio(kickSlug).then((text) => {
      if (!cancelled) setKickBio(text);
    });
    return () => {
      cancelled = true;
    };
  }, [hasKickAbout, kickSlug]);

  const about = useMemo(() => {
    if (!kickBio) return hubAbout;
    return partitionPersonCuriosities({
      ...hub,
      curiosities: [
        {
          id: `kick-bio-${kickSlug}`,
          source: 'kick',
          kind: 'about',
          title: 'About',
          text: kickBio,
          url: `https://kick.com/${kickSlug}`,
        },
        ...(hub.curiosities || []),
      ],
    }, {
      preferredPlatform: liveEvent?.platform || 'kick',
      hasCreatorChannel: true,
    }).about;
  }, [hub, hubAbout, kickBio, kickSlug, liveEvent]);

  if (!personHubHasSurface(hub, results)) return null;

  return (
    <div className="entity-hub-person-hero-wrap">
      <div
        className={`entity-hub-panel entity-hub-hero entity-hub-person-cover${coverSrc ? ' entity-hub-person-cover--has-image' : ''}`}
      >
        {coverSrc ? (
          <div className="entity-hub-person-cover-media">
            <img
              className="entity-hub-person-cover-photo"
              src={coverSrc}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = 'none';
                event.currentTarget.closest('.entity-hub-person-cover')?.classList.remove(
                  'entity-hub-person-cover--has-image',
                );
              }}
            />
            {liveTint ? (
              <div
                className={`entity-hub-person-cover-tint entity-hub-person-cover-tint--${liveTint}`}
                aria-hidden="true"
              />
            ) : null}
          </div>
        ) : null}

        {showFollow || remindEvent ? (
          <div className="entity-hub-hero-actions">
            {showFollow ? <HubFollowButton entityId={entityId} /> : null}
            <HubRemindButton epgEvent={remindEvent} />
          </div>
        ) : null}
        <PersonAboutOverlay about={about} />

        {cover.caption ? (
          cover.href ? (
            <a
              className={metaClass}
              href={cover.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {cover.caption}
            </a>
          ) : (
            <div className={metaClass}>{cover.caption}</div>
          )
        ) : null}

        <div className="entity-hub-person-cover-foot">
          {entity.name ? (
            <h2 className="entity-hub-person-name">{entity.name}</h2>
          ) : null}
          {showLinks ? (
            <HubLinkBadges links={coverLinks} className="entity-hub-person-cover-links" />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PersonHub({ hub, results = [], entityId }) {
  if (!personHubHasSurface(hub, results)) return null;

  const id = entityId || hub?.entity?.id;

  return (
    <div className="entity-hub-grid entity-hub-grid--person entity-hub-grid--single">
      <PersonHero hub={hub} results={results} entityId={id} />
    </div>
  );
}

export default PersonHub;
